import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import {
    clearCurrentQuestionnaireAnswers,
    clearQuestionnaireAnswers,
    clearQuestionnaireContracts,
    deleteQuestionnaireAnswer,
    loadQuestionnaireAnswersByParticipant,
    loadQuestionnaireContractByContract,
    loadQuestionnaires,
    onDeleteQuestionnaireAnswer
} from '../../../store/participantService';
import { formatTime } from '../../../utils/dateFunctions';
import {
    DEFAULT_TABLE_ROWS_PER_PAGE,
    EDIT_ON_CLOSED_ROLES
} from '../../../utils/formValidation/commonConstants';
import { stableSort } from '../../../utils/sortFunctions';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import SingleSelect from '../../formElements/SingleSelect';
import ResultsTable from '../../table/ResultsTable';

import QuestionnaireAnswer from './QuestionnaireAnswer';
import QuestionnaireRow from './QuestionnaireRow';

import classes from '../questionnaire/questionnaire.module.css';

const headCells = [
    { id: 'code', numeric: false, label: 'ID' },
    { id: 'title', numeric: false, label: 'Type' },
    { id: 'dateStarted', numeric: false, label: 'Date Started' },
    { id: 'dateCompleted', numeric: true, label: 'Completed Date' },
    { id: 'timeCompleted', numeric: true, label: 'Completed Time' },
    { id: 'draft', numeric: false, label: 'Status', sortable: false }
];

const initialRowMetaData = {
    order: 'asc',
    orderBy: 'title',
    page: 0,
    rowsPerPage: DEFAULT_TABLE_ROWS_PER_PAGE
};

const deleteMsg = `Questionnaire deleted`;

const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];

const initialRowState = {
    id: null,
    questionnaireId: '',
    participantId: '',
    code: '',
    pdfName: '',
    dateStarted: '',
    dateCompleted: '',
    draft: false,
    data: {}
};

const Questionnaire = ({ isViewOnly = false }) => {
    // HOOKS
    const dispatch = useDispatch();
    const surveyRef = useRef(null);

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [selectedRow, setSelectedRow] = useState({});
    const [showForm, setShowForm] = useState(false);
    const [questionnaireMenuItems, setQuestionnaireMenuItems] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [isDisabled, setIsDisabled] = useState(true);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const currentParticipant = useSelector(
        (state) => state.entities.participantService.currentParticipant
    );
    const questionnaires = useSelector((state) => state.entities.participantService.questionnaires);
    const questionnaireAnswers = useSelector(
        (state) => state.entities.participantService.questionnaireAnswers
    );
    const questionnaireContracts = useSelector(
        (state) => state.entities.participantService.questionnaireContracts
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(() => {
        dispatch(loadQuestionnaires());
        return () => cleanUp();
    }, []);

    useEffect(() => {
        if (showForm) return;
        setRows([]);
        if (currentParticipant?.id && roles.length) {
            dispatch(loadQuestionnaireContractByContract(currentParticipant.contractId));
            dispatch(loadQuestionnaireAnswersByParticipant(currentParticipant.id));

            const isParticipantClosedForEdit =
                currentParticipant.status === 'Closed' && !hasRole(EDIT_ON_CLOSED_ROLES, roles);
            setIsDisabled(
                !hasRole(acceptedRoles, roles) || isParticipantClosedForEdit || isViewOnly
            );
        }
    }, [showForm, currentParticipant, roles]);

    useEffect(() => {
        if (questionnaireAnswers && questionnaireAnswers?.length > 0) {
            const rows = questionnaireAnswers.map((el) => ({
                ...el,
                title: questionnaires.find((entry) => entry.id === el.questionnaireId)?.title || '',
                timeCompleted: formatTime(el?.dateCompleted) || ''
            }));
            setRows(rows);
        }

        setQuestionnaireMenuItems(
            questionnaires
                .filter((el) => !el.inactive)
                .filter((el) =>
                    questionnaireContracts.questionnaires?.some((contract) => contract.id === el.id)
                )
                .filter((el) => {
                    const incompleteQuestionnaire =
                        questionnaireAnswers?.length > 0 &&
                        questionnaireAnswers.find(
                            (entry) => el.id === entry.questionnaireId && entry.draft
                        );
                    return !incompleteQuestionnaire;
                })
                .map((el) => ({
                    ...el,
                    name: el.title
                }))
                .sort((a, b) => a.name?.trim().localeCompare(b.name?.trim()))
        );
    }, [questionnaires, questionnaireContracts, questionnaireAnswers]);

    useEffect(() => {
        if (
            successMessage === 'Questionnaire answers have been completed successfully' ||
            successMessage === 'Questionnaire answers have been drafted successfully'
        ) {
            onQuestionnaireIdChange('');
            dispatch(loadQuestionnaireAnswersByParticipant(currentParticipant.id));
        } else if (successMessage === deleteMsg)
            dispatch(onDeleteQuestionnaireAnswer(selectedRow.id));
    }, [successMessage]);

    // HELPER FNS
    const createRows = () => {
        return stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <QuestionnaireRow
                key={el.id}
                row={el}
                roles={roles}
                showForm={showForm}
                onEdit={onEdit}
                onDelete={onDelete}
                isDisabled={isDisabled}
            />
        ));
    };

    const cleanUp = () => {
        dispatch(clearQuestionnaireContracts());
        dispatch(clearQuestionnaireAnswers());
        dispatch(clearCurrentQuestionnaireAnswers());
    };

    // EVENT HANDLERS
    const onQuestionnaireIdChange = (chosenId) => {
        setSelectedRow({
            ...initialRowState,
            questionnaireId: chosenId
        });
    };

    const onStartQuestionnaire = () => {
        dispatch(clearQuestionnaireAnswers());
        dispatch(clearCurrentQuestionnaireAnswers());
        setShowForm(true);
    };

    const onDelete = (row) => {
        dispatch(deleteQuestionnaireAnswer(row.id, deleteMsg));
    };

    const onEdit = (e, row) => {
        e.stopPropagation();
        setSelectedRow(row);
        setShowForm(true);

        surveyRef.current.scrollIntoView();
    };

    const onClose = () => {
        setShowForm(false);
        onQuestionnaireIdChange('');
    };

    // RENDER
    return (
        <div className={classes.questionnaires} data-testid="questionnaire">
            <div className={classes.tableSection}>
                {rows.length < 1 ? (
                    <div>No questionnaires found</div>
                ) : (
                    <ResultsTable
                        defaultOrderBy={rowMetaData.orderBy}
                        sortOrder={rowMetaData.order}
                        headCells={headCells}
                        passRowMetaDataUp={setRowMetaData}
                        tableRows={createRows()}
                        totalResults={rows.length}
                    />
                )}
            </div>
            <div>
                {!showForm && (
                    <>
                        <SingleSelect
                            id={'questionnaireId'}
                            label={'Questionnaire (In-Progress questionnaires will be omitted)'}
                            placeholder="Please select a questionnaire..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={questionnaireMenuItems}
                            selectedId={selectedRow.questionnaireId || ''}
                            selected={
                                questionnaireMenuItems.find(
                                    (el) => el.id === selectedRow.questionnaireId
                                ) || {}
                            }
                            onChange={(chosenId) => onQuestionnaireIdChange(chosenId)}
                        />
                        <Button
                            id="StartQuestionnaireButton"
                            disabled={isDisabled || !selectedRow.questionnaireId || showForm}
                            type="button"
                            content="Start Questionnaire"
                            onClick={onStartQuestionnaire}
                        />
                    </>
                )}
                <div ref={surveyRef}>
                    {showForm && (
                        <QuestionnaireAnswer row={selectedRow} onClose={onClose} roles={roles} />
                    )}
                </div>
            </div>
        </div>
    );
};

Questionnaire.propTypes = {
    isViewOnly: PropTypes.bool
};

export default Questionnaire;
